import React from 'react';
import PropTypes from 'prop-types';

import {Box, Skeleton, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useNavigationColumnById} from '@amblique/contentful/app/contentful/hooks/use-navigation-column';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';

/**
 * @override
 * NavigationColumn component from Contentful
 *
 * NavigationColumn represents a column in a navigation menu with:
 * - title: Column title (required)
 * - headerText: Alternative header text (optional)
 * - linksCollection: Array of Link entries
 *
 * This component auto-fetches nested data (linksCollection) if missing,
 * following the Three-Tier Fragment Strategy pattern.
 *
 * @param {Object} props - Component props
 * @param {Contentful.NavigationColumn} props.component - NavigationColumn component data
 * @param {boolean} [props.isComplete] - Whether component has all fields (from parent)
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched (from parent)
 * @returns {React.ReactElement}
 */
export const NavigationColumn = ({
  component,
  hideImages = false,
  isComplete: parentIsComplete = true,
  isFetching: parentIsFetching = false,
  ...props
}) => {
  const hasLinksCollection = component?.linksCollection !== undefined && component?.linksCollection !== null;
  const localIsComplete = hasLinksCollection;

  const shouldFetch = !localIsComplete && Boolean(component?.sys?.id);

  const {
    data: fullNavigationColumn,
    isFetching: isLocalFetching,
  } = useNavigationColumnById(component?.sys?.id || null, {
    variant: 'default',
    queryOptions: {
      enabled: shouldFetch,
      retry: false,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: false,
    },
  });

  const finalComponent = fullNavigationColumn || component;
  const isFinalComplete = hasLinksCollection || Boolean(fullNavigationColumn?.linksCollection);
  const isFinalFetching = parentIsFetching || isLocalFetching;

  const componentForStyles = finalComponent || {};

  const styles = useMultiStyleConfig('MegaMenu', {
    component: componentForStyles,
    isComplete: isFinalComplete,
    isFetching: isFinalFetching,
  });

  // Show skeleton while fetching
  if (!component || (!isFinalComplete && isFinalFetching)) {
    return (
      <Box className="navigation-column navigation-column-skeleton" {...styles.navigationColumnWrapper} {...props}>
        {/* Skeleton for title */}
        <Skeleton
          height="14px"
          width="80px"
          {...styles.navigationColumnTitleSkeleton}
        />
        {/* Skeleton for links - show 4 skeleton items */}
        <Stack {...styles.navigationColumnLinksWrapper}>
          {[1, 2, 3, 4].map((index) => (
            <Skeleton
              key={`skeleton-link-${index}`}
              height="20px"
              width="90px"
              {...styles.navigationColumnLinkSkeleton}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  const {linksCollection} = finalComponent;
  const links = (linksCollection?.items || []).filter(Boolean);

  return (
    <Box className="navigation-column" {...styles.navigationColumnWrapper} {...props}>
      <Text {...styles.navigationColumnTitle}>
        {component?.headerText}
      </Text>
      {links.length > 0 && (
        <Stack className="navigation-column-links" {...styles.navigationColumnLinksWrapper}>
          {links.map((link) => {
            if (!link) return null;

            const linkComponent = hideImages ?
              {
                ...link,
                image: undefined,
                iconPosition: undefined,
                linkType: undefined,
                type: undefined,
              } :
              link;

            return (
              <ContentfulLink key={link.sys?.id} component={linkComponent} />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

NavigationColumn.displayName = 'NavigationColumn';
NavigationColumn.propTypes = {
  component: PropTypes.object.isRequired,
  hideImages: PropTypes.bool,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
